import React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  EditSpendingPolicyFormFragment,
  UpdateSpendingPolicyDocument,
  UpdateSpendingPolicyMutationVariables,
} from 'generated/resources/graphql'

import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import IconSelector from './IconSelector'

interface Props {
  spendingPolicy: EditSpendingPolicyFormFragment
  onSuccess?: () => void
}

export const SpendingPolicyForm: React.FC<Props> = ({ spendingPolicy, onSuccess }) => {
  const [updateSpendingPolicy] = useMutation(UpdateSpendingPolicyDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceSpendingPolicySpendingPolicy,
  })

  const { isSubmitting, values, errors, setErrors, setFieldValue, handleSubmit, isValid, dirty } =
    useForm<UpdateSpendingPolicyMutationVariables>({
      enableReinitialize: true,

      defaultValues: {
        id: spendingPolicy.id.toString(),
        name: spendingPolicy.name,
        icon: spendingPolicy.icon,
        description: spendingPolicy.description,
      },

      validate: (data) => {
        const validationErrors: FormErrors<UpdateSpendingPolicyMutationVariables> = {}

        if (!data.name) {
          validationErrors.name = i18n.t('common.empty')
        }

        return validationErrors
      },

      onSubmit: async (formValues: UpdateSpendingPolicyMutationVariables) => {
        let handled = null

        const result = await updateSpendingPolicy({
          variables: formValues,
        })
        handled = handleResult(result, (submitErrors) => {
          if (submitErrors) {
            setErrors(submitErrors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('spending_policies.details.form.update_error'),
            })
          }
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('spending_policies.details.form.update_success'),
          })

          onSuccess?.()
        }
        return handled
      },
    })
  return (
    <Box gap="s24" paddingBottom="s24">
      <IconSelector
        defaultIcon={values.icon}
        setIcon={(value: string) => setFieldValue('icon', value)}
      />
      <TextField
        mandatory
        label={i18n.t('spending_policies.details.form.name.label')}
        description={i18n.t('spending_policies.details.form.name.description')}
        value={values.name}
        onChange={(value) => setFieldValue('name', value)}
        error={errors.name}
      />

      <Box gap="s8">
        <Box gap="s2">
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('spending_policies.details.form.intro.label')}
            </Text.Normal>
          </Box>

          <Box flexDirection="row">
            <Text.Normal size="200">
              {i18n.t('spending_policies.details.form.intro.description')}
            </Text.Normal>
          </Box>
        </Box>

        <TextEditor
          height="s200"
          placeholder=""
          onChange={(value) => {
            setFieldValue('description', String(value ?? ''))
          }}
          initialEditorState={{
            data: values.description || '',
          }}
        />
      </Box>

      {dirty && (
        <Box alignItems="flexEnd">
          <Button loading={isSubmitting} onClick={() => handleSubmit()} disabled={!isValid}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
